import React, { useState } from "react";
import Modal from "react-modal";
import { useLocationEditor } from "./useLocationEditor";
import EditAbout from "./about";
import EditMenu from "./menu";
import EditReviews from "./reviews";
import CollapsibleSection from "./components/collapsibleSection";
import { fetchMenuCandidates } from "../utils/findMenuUrl";
import { runMenuScrapeChain } from "../utils/menuScrapeChain";
import { useLanguage } from "../../../LanguageContext";
import * as text from "../../../translation.json";

const type = {
  restaurant: "Restaurant",
  activity: "Aktivitet",
  event: "Arrangement",
};

// Combines About + Menu (+ NewMenuTextDemo, rendered internally by EditMenu) +
// Reviews for a single location on one page, all reusing the existing
// controlled components as-is. State/save logic comes from useLocationEditor,
// which mirrors editMyLocation.js's onSaveChanges/onActivateLocation/onDeactivateLocation.
const LocationQuickEditPanel = ({
  locationId,
  currentBusinessID,
  userInformation,
  onSaved,
}) => {
  const { language } = useLanguage();
  const editor = useLocationEditor({ locationId, currentBusinessID });
  const [openActivateModal, setOpenActivateModal] = useState(false);
  const [menuLinkTest, setMenuLinkTest] = useState(null);
  // "idle" | "saving" | "saved" | "error" — shown next to the save button
  // instead of the confirm modal editMyLocation.js uses.
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);

  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      await editor.onSaveChanges();
      setSavedAt(
        new Date().toLocaleTimeString("nb-NO", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
      setSaveStatus("saved");
      onSaved && onSaved();
    } catch (err) {
      console.error("Quick review save failed:", err);
      setSaveStatus("error");
    }
  };

  const testFindMenuLink = async () => {
    setMenuLinkTest({ loading: true });
    try {
      const candidates = await fetchMenuCandidates(
        editor.locationInformation.website,
      );
      setMenuLinkTest({ loading: false, candidates });
    } catch (err) {
      setMenuLinkTest({ loading: false, error: err.message });
    }
  };

  const applyMenuCandidate = async (candidateUrl) => {
    editor.setMenuFetchUrl(candidateUrl);
    setMenuLinkTest((prev) => ({
      ...prev,
      applying: candidateUrl,
      applyResult: null,
    }));

    const result = await runMenuScrapeChain({
      url: candidateUrl,
      ignoreTerms: editor.ignoreTerms,
      includeTerms: editor.includeTerms,
    });

    if (result.success) {
      editor.setMenuList(result.menuList);
      editor.setMenuFetchUrlType(result.method);
    }

    setMenuLinkTest((prev) => ({
      ...prev,
      applying: null,
      applyResult: { url: candidateUrl, ...result },
    }));
  };

  if (editor.loading) {
    return <div className='box'>Loading...</div>;
  }

  const {
    locationInformation,
    setLocationinformation,
    businessOwner,
    editLanguage,
    setEditLanguage,
    newProfileImage,
    fileImage,
    textInformation,
    menuList,
    tripadvisorLink,
    yelpLink,
    googleBusinessDetails,
    onActivateLocation,
    onDeactivateLocation,
  } = editor;

  if (!(businessOwner || userInformation?.admin)) {
    return <div className='box'>Access Denied!</div>;
  }

  const aboutFlagged =
    !(newProfileImage && newProfileImage.length > 1) &&
    !Object.values(fileImage).some((x) => x && x.length > 0)
      ? true
      : textInformation.description.length === 0 ||
        textInformation.tags.length === 0;

  const menuFlagged = !menuList || menuList.length === 0;

  const reviewsFlagged =
    !(tripadvisorLink?.length > 0) &&
    !(yelpLink?.length > 0) &&
    !googleBusinessDetails;

  return (
    <div
      className='box quickReviewPanel quickReviewPanelOpen'
      style={{ marginBottom: "15px" }}>
      <h2>
        {locationInformation.locationName}{" "}
        <label
          style={{
            fontSize: 14,
            color:
              locationInformation.type === "restaurant"
                ? "#5d3160"
                : locationInformation.type === "activity"
                  ? "#52c78c"
                  : "#4550e2",
          }}>
          {type[locationInformation.type]}
        </label>
        {userInformation?.moderator && (
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(
              locationInformation.locationName,
            )}`}
            target='_blank'
            rel='noopener noreferrer'>
            ↗️
          </a>
        )}
      </h2>
      {!userInformation?.studentOrganisasjon && (
        <div style={{ marginBottom: "10px" }}>
          <label
            style={{
              color: locationInformation.active ? "#52c78c" : "#f9615b",
              fontWeight: "500",
            }}>
            {locationInformation.active
              ? text[language].active
              : text[language].notactive}
          </label>
          <button
            className='universalButton'
            style={{ marginLeft: "10px" }}
            onClick={() => setOpenActivateModal(true)}>
            {locationInformation.active
              ? text[language].deactivate
              : text[language].activate}
          </button>
        </div>
      )}
      {userInformation?.moderator && (
        <div style={{ marginBottom: "10px" }}>
          <label
            style={{
              color: locationInformation.toReview ? "#52c78c" : "#f9615b",
              fontWeight: "500",
            }}>
            {locationInformation.toReview
              ? text[language].inreview
              : text[language].notinreview}
          </label>
          <button
            className='universalButton'
            style={{ marginLeft: "10px" }}
            onClick={() =>
              setLocationinformation({
                ...locationInformation,
                toReview: !locationInformation.toReview,
              })
            }>
            {locationInformation.toReview
              ? text[language].notinreview
              : text[language].settTilreview}
          </button>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={() =>
            setEditLanguage(editLanguage !== "english" ? "english" : "norwegian")
          }
          className='editMenuButtons'
          style={
            editLanguage === "english"
              ? { color: "#f9615b", backgroundColor: "white", border: "white" }
              : {}
          }>
          {text[language].engelsk}
        </button>
      </div>

      {userInformation?.admin && (
        <div className='quickReviewMenuLinkTest'>
          <button
            className='universalButton'
            disabled={!locationInformation.website || menuLinkTest?.loading}
            onClick={testFindMenuLink}>
            {menuLinkTest?.loading
              ? "Searching..."
              : "Test: Find menu link"}
          </button>
          {!locationInformation.website && (
            <span className='quickReviewMenuLinkTestHint'>
              No website registered
            </span>
          )}
          {menuLinkTest?.error && (
            <div className='quickReviewMenuLinkTestError'>
              {menuLinkTest.error}
            </div>
          )}
          {menuLinkTest?.candidates && (
            <div className='quickReviewMenuLinkTestResults'>
              {menuLinkTest.candidates.length === 0 ? (
                <div>No menu link found.</div>
              ) : (
                menuLinkTest.candidates.map((c, i) => (
                  <div
                    key={c.url}
                    className={`quickReviewMenuLinkTestRow${i === 0 ? " best" : ""}`}>
                    <span className='quickReviewMenuLinkTestType'>
                      {c.type} ({c.score})
                    </span>
                    <button
                      className='editPageLink'
                      style={{ background: "none", border: "none", padding: 0 }}
                      disabled={!!menuLinkTest.applying}
                      onClick={() => applyMenuCandidate(c.url)}>
                      {menuLinkTest.applying === c.url
                        ? "Trying..."
                        : c.text || c.url}
                    </button>
                    <a
                      href={c.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      title='Open in new tab'>
                      ↗
                    </a>
                  </div>
                ))
              )}
            </div>
          )}
          {menuLinkTest?.applyResult && (
            <div
              className={`quickReviewMenuLinkTestApplyResult${
                menuLinkTest.applyResult.success ? " success" : " fail"
              }`}>
              {menuLinkTest.applyResult.success
                ? `✅ Found a menu (${menuLinkTest.applyResult.menuList.length} categories) via ${menuLinkTest.applyResult.method} — check the Menu section below.`
                : `❌ No menu found automatically (tried ${menuLinkTest.applyResult.attempts
                    .map((a) => a.method)
                    .join(
                      ", ",
                    )}). menuFetchUrl is set — try manually in the Menu section.`}
            </div>
          )}
        </div>
      )}

      <div className='quickReviewColumns'>
      <CollapsibleSection
        title={text[language].om}
        flagged={aboutFlagged}
        defaultOpen>
        <EditAbout
          regularOpeningHours={editor.regularOpeningHours}
          setRegularOpeningHours={editor.setRegularOpeningHours}
          setRegularClosingHours={editor.setRegularClosingHours}
          regularClosingHours={editor.regularClosingHours}
          newCustomOpeningHours={editor.newCustomOpeningHours}
          setNewCustomOpeningHours={editor.setNewCustomOpeningHours}
          locationInfo={locationInformation}
          newProfileImage={editor.newProfileImage}
          setNewProfileImage={editor.setNewProfileImage}
          fileImage={editor.fileImage}
          setFileImage={editor.setFileImage}
          fileName={editor.fileName}
          setFileName={editor.setFileName}
          textInformation={editor.textInformation}
          setTextInformation={editor.setTextInformation}
          newProfileFile={editor.newProfileFile}
          setNewProfileFile={editor.setNewProfileFile}
          newProfileImagePreview={editor.newProfileImagePreview}
          setNewProfileImagePreview={editor.setNewProfileImagePreview}
          editLanguage={editLanguage}
          irrOpeninghours={editor.irrOpeninghours}
          setIrrOpeningHours={editor.setIrrOpeningHours}
          irrOpeninghoursText={editor.irrOpeninghoursText}
          setIrrOpeninghoursText={editor.setIrrOpeninghoursText}
          userInformation={userInformation}
          suggestedTags={editor.suggestedTags}
          setSuggestedTags={editor.setSuggestedTags}
          googleBusinessDetails={editor.googleBusinessDetails}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title={
          locationInformation.type === "restaurant"
            ? text[language].meny
            : text[language].Activities
        }
        flagged={menuFlagged}
        defaultOpen>
        <EditMenu
          locationId={locationId}
          locationInfo={locationInformation}
          newMenu={editor.newMenu}
          setNewMenu={editor.setNewMenu}
          newMenuEnglish={editor.newMenuEnglish}
          setNewMenuEnglish={editor.setNewMenuEnglish}
          lokasjonstype={locationInformation.type}
          menuList={editor.menuList}
          setMenuList={editor.setMenuList}
          menuListEnglish={editor.menuListEnglish}
          setMenuListEnglish={editor.setMenuListEnglish}
          editLanguage={editLanguage}
          textInformation={editor.textInformation}
          setTextInformation={editor.setTextInformation}
          menuFetchUrl={editor.menuFetchUrl}
          setMenuFetchUrl={editor.setMenuFetchUrl}
          menuFetchSelectors={editor.menuFetchSelectors}
          setMenuFetchSelectors={editor.setMenuFetchSelectors}
          menuFetchAvailable={editor.menuFetchAvailable}
          setMenuFetchAvailable={editor.setMenuFetchAvailable}
          menuFetchUrlEnglish={editor.menuFetchUrlEnglish}
          setMenuFetchUrlEnglish={editor.setMenuFetchUrlEnglish}
          config={editor.config}
          setConfig={editor.setConfig}
          ignoreTerms={editor.ignoreTerms}
          setIgnoreTerms={editor.setIgnoreTerms}
          includeTerms={editor.includeTerms}
          setIncludeTerms={editor.setIncludeTerms}
          menuFetchUrlType={editor.menuFetchUrlType}
          setMenuFetchUrlType={editor.setMenuFetchUrlType}
          suggestedTags={editor.suggestedTags}
          setSuggestedTags={editor.setSuggestedTags}
        />
      </CollapsibleSection>

      {editLanguage !== "english" && (
        <CollapsibleSection
          title={text[language].omtaler}
          flagged={reviewsFlagged}
          defaultOpen>
          <EditReviews
            locationInfo={locationInformation}
            googleBusinessDetails={editor.googleBusinessDetails}
            setGoogleBusinessDetails={editor.setGoogleBusinessDetails}
            tripadvisorLink={editor.tripadvisorLink}
            setTripadvisorLink={editor.setTripadvisorLink}
            yelpLink={editor.yelpLink}
            setYelpLink={editor.setYelpLink}
          />
        </CollapsibleSection>
      )}
      </div>

      <div className='quickReviewSaveRow'>
        <button
          onClick={handleSave}
          disabled={saveStatus === "saving"}
          className='universalButtonRed'
          style={{ width: "180px", margin: 0 }}>
          {saveStatus === "saving" ? "Saving..." : text[language].savechanges}
        </button>
        {saveStatus === "saved" && (
          <span className='quickReviewSaveStatus saved'>
            ✓ Changes saved at {savedAt}
          </span>
        )}
        {saveStatus === "error" && (
          <span className='quickReviewSaveStatus error'>
            ✗ Save failed — try again
          </span>
        )}
      </div>

      <Modal
        isOpen={openActivateModal}
        onRequestClose={() => setOpenActivateModal(false)}
        className='modalStyle'
        style={{ content: { width: "17%" } }}>
        {locationInformation.active ? (
          <div className='editActiveModal'>
            <label className='editPageLabel'>{text[language].sureDeactivate}</label>
            <div className='editActiveModalButtonContainer'>
              <button
                onClick={async () => {
                  await onDeactivateLocation();
                  setOpenActivateModal(false);
                  onSaved && onSaved();
                }}
                className='universalButton'
                style={{ width: "70%", margin: 0 }}>
                {text[language].yesdeactivate}
              </button>
              <button
                onClick={() => setOpenActivateModal(false)}
                className='universalButtonRed'
                style={{ width: "70%", marginTop: 10, marginBottom: 0 }}>
                {text[language].notakeback}
              </button>
            </div>
          </div>
        ) : (
          <div className='editActiveModal'>
            <label className='editPageLabel'>{text[language].sureActivate}</label>
            <div className='editActiveModalButtonContainer'>
              <button
                onClick={async () => {
                  await onActivateLocation();
                  setOpenActivateModal(false);
                  onSaved && onSaved();
                }}
                className='universalButtonRed'
                style={{ width: "70%", margin: 0 }}>
                {text[language].yesactivate}
              </button>
              <button
                onClick={() => setOpenActivateModal(false)}
                className='universalButton'
                style={{ width: "70%", marginTop: 10, marginBottom: 0 }}>
                {text[language].notakeback}
              </button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default LocationQuickEditPanel;
